import {
  ComponentType,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import useDeepCompareEffect from 'react-use/lib/useDeepCompareEffect';

import { useSearchParamsWithValues } from '../../hooks/useSearchParamsWithValues';
import { useLocalStorage } from '../../hooks/useStorage';
import { SearchUtils } from '../../utils';

export interface FilterEditorComponentProps<T> {
  filter: Filter<T>;
  value: T | null;
  onValueChange: (value: T) => void;
  onClose: () => void;
}

export interface FilterViewerComponentProps<T> {
  filter: Filter<T>;
  value: T;
  onValueChange: (value: T) => void;
  onRemove: () => void;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Filter is heterogeneous; T appears in contravariant positions (editor/viewer/query callbacks). `unknown` default rejects Filter<string[]> assignments throughout consumers.
export interface Filter<T = any> {
  id: string;
  label: string;
  category?: string;
  hotkey?: string;
  placement?: 'inline' | 'overflow'; // Where the filter is rendered in the FilterRow
  editor: ComponentType<FilterEditorComponentProps<T>>;
  viewer: ComponentType<FilterViewerComponentProps<T>>;
  query: (value: T) => string;
}

export function createFilter<T>(filter: Filter<T>) {
  return filter;
}
interface StoredFilter<T> {
  id: string;
  value: T;
}

export interface ActiveFilter<T> extends Filter<T> {
  value: T;
}

/* eslint-disable @typescript-eslint/no-explicit-any -- Filter<any> needed for heterogeneous arrays; T is contravariant so unknown rejects Filter<string[]> at consumer call sites */
type FilterContext = {
  query: string;
  setQuery: (query: string) => void;
  filters: Filter<any>[];
  activeFilters: ActiveFilter<any>[];
  addFilter: (filter: Filter<any>, value: unknown) => void;
  removeFilter: (id: string) => void;
  resetFilters: () => void;
  searchField: string;
};
/* eslint-enable @typescript-eslint/no-explicit-any */

export const FILTER_CONTEXT = createContext<FilterContext>({
  filters: [],
  query: '',
  setQuery: () => {},
  activeFilters: [],
  addFilter: () => {},
  removeFilter: () => {},
  resetFilters: () => {},
  searchField: '',
});

export function FiltersProvider({
  filters = [],
  children,
  storageKey = 'filter-list',
  searchField = 'displayName',
  defaultFilters = [],
  disableQueryParams = false,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- heterogeneous Filter array; see FilterContext above
  filters?: Filter<any>[];
  storageKey: string;
  children: ReactNode;
  searchField: string;
  defaultFilters?: Array<{ id: string; value: unknown }>;
  disableQueryParams?: boolean;
}) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- heterogeneous Filter array; see FilterContext above
  const [stateStorage, setStateStorage] = useLocalStorage<StoredFilter<any>[]>(
    `filter-list-${storageKey}`,
    defaultFilters,
  );
  const { searchParams, setSearchParams } = useSearchParamsWithValues();
  const effectiveSearchParams = useMemo(
    () => (disableQueryParams ? {} : searchParams),
    [disableQueryParams, searchParams],
  );
  const effectiveSetSearchParams = useMemo(
    () => (disableQueryParams ? () => {} : setSearchParams),
    [disableQueryParams, setSearchParams],
  );
  // current active filters. initially sourced from local storage, then query params, then default filters
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- heterogeneous Filter array; see FilterContext above
  const [activeFilters, setActiveFilters] = useState<ActiveFilter<any>[]>(
    () => {
      const storedFilters = Object.fromEntries(
        stateStorage.map(f => [f.id, f.value]),
      );
      // first check if there are any filters in the query param that are relevant
      const queryFilters = filters
        .filter(f => effectiveSearchParams[f.id] !== undefined)
        .map(f => ({
          ...f,
          value:
            typeof effectiveSearchParams[f.id] === 'string'
              ? (effectiveSearchParams[f.id] as string).split(',')
              : effectiveSearchParams[f.id],
        }));

      const filtersFromStorage = filters
        .filter(f => storedFilters[f.id] !== undefined)
        .map(f => ({
          ...f,
          value: storedFilters[f.id],
        }));

      return !queryFilters.length ? filtersFromStorage : queryFilters;
    },
  );

  // Track filter IDs to detect newly added filters
  const knownFilterIdsRef = useRef<Set<string>>(
    new Set(filters.map(f => f.id)),
  );

  // Restore filters that are added dynamically (e.g., label filters loaded async)
  useEffect(() => {
    const currentIds = new Set(filters.map(f => f.id));
    const newFilters = filters.filter(
      f => !knownFilterIdsRef.current.has(f.id),
    );

    if (newFilters.length > 0) {
      const storedFilters = Object.fromEntries(
        stateStorage.map(f => [f.id, f.value]),
      );

      // Prefer URL params over storage
      const filtersFromUrl = newFilters
        .filter(f => effectiveSearchParams[f.id] !== undefined)
        .map(f => ({
          ...f,
          value:
            typeof effectiveSearchParams[f.id] === 'string'
              ? (effectiveSearchParams[f.id] as string).split(',')
              : effectiveSearchParams[f.id],
        }));

      const filtersFromStorage = newFilters
        .filter(
          f =>
            storedFilters[f.id] !== undefined &&
            effectiveSearchParams[f.id] === undefined,
        )
        .map(f => ({
          ...f,
          value: storedFilters[f.id],
        }));

      const filtersToRestore = [...filtersFromUrl, ...filtersFromStorage];

      if (filtersToRestore.length > 0) {
        setActiveFilters(current => {
          const existingIds = new Set(current.map(f => f.id));
          const toAdd = filtersToRestore.filter(f => !existingIds.has(f.id));
          return [...current, ...toAdd];
        });
      }
    }

    knownFilterIdsRef.current = currentIds;
  }, [filters, effectiveSearchParams, stateStorage]);

  const [query, setQuery] = useState(effectiveSearchParams.query ?? '');

  useDeepCompareEffect(() => {
    // Update state storage only if there are no search params
    if (Object.keys(effectiveSearchParams).length === 0) {
      setStateStorage(activeFilters.map(f => ({ id: f.id, value: f.value })));
    }

    const params = activeFilters.reduce<Record<string, string>>((acc, f) => {
      return { ...acc, [f.id]: f.value };
    }, {});

    params.query = query;

    effectiveSetSearchParams(params);
  }, [activeFilters, query]);

  const addFilter = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- heterogeneous Filter array; see FilterContext above
    (filter: Filter<any>, value: unknown) => {
      setActiveFilters(currentFilters => {
        // try to keep the existing filter if it exists, in the same order
        const existingFilter = currentFilters.find(f => f.id === filter.id);
        if (existingFilter) {
          const updatedFilters = currentFilters.map(f =>
            f.id === filter.id ? { ...f, value } : f,
          );
          setStateStorage(
            updatedFilters.map(f => ({ id: f.id, value: f.value })),
          );
          return updatedFilters;
        }

        // otherwise, add the new filter to the end of the list
        const newFilters = [...currentFilters, { ...filter, value }];
        setStateStorage(newFilters.map(f => ({ id: f.id, value: f.value })));
        return newFilters;
      });
    },
    [setStateStorage],
  );

  const removeFilter = useCallback(
    (id: string) => {
      setActiveFilters(currentFilters => {
        const newFilters = currentFilters.filter(f => f.id !== id);
        setStateStorage(newFilters.map(f => ({ id: f.id, value: f.value })));
        return newFilters;
      });
    },
    [setStateStorage],
  );

  const resetFilters = useCallback(() => {
    setActiveFilters([]);
    setQuery('');
    setStateStorage([]);
    effectiveSetSearchParams({});
  }, [setQuery, setStateStorage, effectiveSetSearchParams]);

  const value = useMemo(
    () => ({
      filters,
      activeFilters,
      addFilter,
      removeFilter,
      query,
      setQuery,
      searchField,
      resetFilters,
    }),
    [
      filters,
      activeFilters,
      addFilter,
      removeFilter,
      query,
      setQuery,
      searchField,
      resetFilters,
    ],
  );

  return (
    <FILTER_CONTEXT.Provider value={value}>{children}</FILTER_CONTEXT.Provider>
  );
}

export function useFilters() {
  const context = useContext(FILTER_CONTEXT);

  if (!context)
    throw new Error('useFilterList must be used within a FilterListProvider');

  const {
    searchField,
    filters,
    activeFilters,
    query,
    resetFilters,
    setQuery,
    addFilter,
    removeFilter,
  } = context;

  const filter = useMemo(() => {
    const parts = activeFilters
      .map(f => f.query(f.value))
      .filter(Boolean)
      .map(f => `(${f})`);

    if (query !== '') {
      const escaped = SearchUtils.escapeQuery(query);
      parts.push(`(${searchField}:${escaped}*)`);
    }

    return parts.join(' AND ');
  }, [activeFilters, query, searchField]);

  const isDefaultFilterState = useMemo(() => {
    return activeFilters.length === 0 && !query;
  }, [activeFilters, query]);

  // we return something that looks like the old useListFilter hook
  return {
    searchQuery: query,
    filterString: filter,
    filters,
    activeFilters,
    resetFilters,
    isDefaultFilterState,

    // Exported to allow usage of FiltersProvider independently of the UI.
    setQuery,
    addFilter,
    removeFilter,
  };
}
